import React, { useState, useEffect } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Typography,
  Tooltip,
  IconButton,
  Box,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import i18next from 'i18next';
import { formatPercentage } from 'components/Percentage';
import { canEditAdminSpaceId, userSelector } from 'helper/security';
import { ECountryCode } from '__generated__/graphql';

interface CancellationRule {
  daysToEvent: number;
  rate: number;
  toleranceRate?: number | null;
}


interface AgbConfirmationDialogProps {
  open: boolean;
  title: string;
  spaceId: number;
  children: React.ReactNode;
  isListerBooking: boolean;
  isPremiumClient: boolean;
  hotelCountry: ECountryCode;
  cancellationRules?: CancellationRule[];
  askSavePrices: boolean;
  onClose: () => void;
  onConfirm: (savePrices: boolean) => void;
}

/**
 * Terms and conditions (AGB) confirmation dialog.
 * Requires user acceptance before proceeding with certain actions.
 */
export default function AgbConfirmationDialog({
  open,
  title,
  spaceId,
  children,
  isListerBooking,
  isPremiumClient,
  hotelCountry,
  cancellationRules = [],
  askSavePrices,
  onClose,
  onConfirm,
}: AgbConfirmationDialogProps) {
  const user = userSelector()!;
  const [agbConfirmed, setAgbConfirmed] = useState(false);
  const [cancellationConfirmed, setCancellationConfirmed] = useState(false);
  const [commissionConfirmed, setCommissionConfirmed] = useState(false);
  const [savePrices, setSavePrices] = useState(true);
  const canSavePrices = canEditAdminSpaceId(user, spaceId);
  const [showSavePrices, setShowSavePrices] = useState(askSavePrices && canSavePrices);

  useEffect(() => {
    if (open) {
      setAgbConfirmed(false);
      setCancellationConfirmed(false);
      setCommissionConfirmed(false);
      setSavePrices(true);
      setShowSavePrices(askSavePrices && canSavePrices);
    }
  }, [open, askSavePrices, canSavePrices]);

  const handleClose = () => {
    setAgbConfirmed(false);
    setCancellationConfirmed(false);
    setCommissionConfirmed(false);
    onClose();
  };

  const handleConfirm = () => {
    setAgbConfirmed(false);
    setCancellationConfirmed(false);
    setCommissionConfirmed(false);
    onConfirm(showSavePrices ? savePrices : false);
  };

  const allCheckboxesConfirmed = !isListerBooking || (agbConfirmed && cancellationConfirmed && commissionConfirmed);

  const renderCancellationTooltip = () => (
    <Box>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {i18next.t('offer-info-text-cancellation')}
      </Typography>
      {cancellationRules.map((rule, index) => (
        <Typography key={index} variant="body2">
          • {i18next.t('offer-info-text-cancellation-rule', {
            daysToEvent: rule.daysToEvent,
            rate: formatPercentage(rule.rate, 0),
          })}
        </Typography>
      ))}
      {cancellationRules.some(r => r.toleranceRate && r.toleranceRate > 0) && (
        <Typography variant="body2" sx={{ mt: 1 }}>
          {i18next.t('offer-info-text-cancellation-rule-waive', {
            toleranceRate: formatPercentage(
              cancellationRules.find(r => r.toleranceRate)!.toleranceRate!,
              0
            ),
          })}
        </Typography>
      )}
    </Box>
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ paddingTop: 1 }}>
          <Grid item xs={12}>
            {children}
          </Grid>
          {isListerBooking && (
            <>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Checkbox checked={agbConfirmed} onChange={(e) => setAgbConfirmed(e.target.checked)} />}
                  label={
                    <>
                      {i18next.t('offer-confirm-agb')}{' '}
                      <a href={hotelCountry === ECountryCode.DE ? 'https://www.seminargo.com/wp-content/uploads/2025/12/AGB_DE_Veranstaltungsvermittlung_2023.pdf' : 'https://www.seminargo.com/wp-content/uploads/2025/12/AGB_AT_Veranstaltungsvermittlung_2023.pdf'} target="_blank" rel="noopener noreferrer">
                        ({i18next.t('offer-confirm-agb-link')})
                      </a>
                    </>
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Checkbox checked={cancellationConfirmed} onChange={e => setCancellationConfirmed(e.target.checked)} />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {cancellationRules.length > 0 ? (
                        <Tooltip
                          title={renderCancellationTooltip()}
                          arrow
                          placement="right"
                          componentsProps={{
                            tooltip: {
                              sx: {
                                maxWidth: 350,
                                bgcolor: 'white',
                                color: 'text.primary',
                                boxShadow: 3,
                                border: '1px solid',
                                borderColor: 'divider',
                                p: 1.5,
                                fontSize: '0.875rem',
                              },
                            },
                          }}
                        >
                          <span style={{ textDecoration: 'underline dotted', cursor: 'pointer' }}>
                            {i18next.t('offer-confirm-cancellation')}
                          </span>
                        </Tooltip>
                      ) : (
                        <span>{i18next.t('offer-confirm-cancellation')}</span>
                      )}
                    </Box>
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Checkbox checked={commissionConfirmed} onChange={(e) => setCommissionConfirmed(e.target.checked)} />}
                  label={i18next.t('offer-confirm-commission')}
                />
              </Grid>
              {isPremiumClient && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                    ℹ️ {i18next.t('offer-confirm-payment-premium')}
                  </Typography>
                </Grid>
              )}
            </>
          )}
          {showSavePrices && (
            <Grid item xs={12}>
              <FormControlLabel
                control={<Checkbox checked={savePrices} onChange={(e) => setSavePrices(e.target.checked)} />}
                label={i18next.t('offerconfirm-approve-offer-save-prices')}
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={handleClose} color="secondary">
          {i18next.t('confirmation-dialog-decline')}
        </Button>
        <Button
          variant="contained"
          disabled={!allCheckboxesConfirmed}
          onClick={handleConfirm}
          color="primary"
        >
          {i18next.t('confirmation-dialog-confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}